<template>
	<Modal @hide="$emit('hide')" max-width="max-w-lg" :hideX="true" class="!items-center">
		<div class="flex flex-col items-center">
			<!-- Header -->
			<div class="text-center mb-2">
				<div class="text-xs font-semibold text-ihh-slate-gray uppercase tracking-widest mb-1">Select Required Options</div>
				<div class="text-sm text-ihh-slate-gray">Product {{ currentIndex + 1 }} of {{ products.length }}</div>
			</div>

			<!-- Progress dots -->
			<div v-if="products.length > 1" class="flex gap-2 mb-4">
				<div
					v-for="(p, i) in products"
					:key="i"
					class="w-2 h-2 rounded-full transition-all duration-300"
					:class="i === currentIndex ? 'bg-ihh-dark-salmon scale-125' : i < currentIndex ? 'bg-ihh-dark-salmon/50' : 'bg-ihh-gray/40'"
				/>
			</div>

			<!-- Product card -->
			<div class="w-full rounded-xl p-4 bg-gradient-to-t from-ihh-purple via-ihh-pink to-ihh-pink shadow">
				<div class="flex items-start gap-4 mb-4">
					<div class="flex items-center justify-center w-20 h-20 min-w-[5rem] rounded-xl p-2 shadow bg-ihh-white">
						<img class="max-w-full max-h-full object-contain" :src="currentThumbnail" @error="onImgError" />
					</div>
					<div class="flex-1">
						<div class="text-sm font-semibold text-ihh-midnight-gray">{{ currentProduct.product.name }}</div>
						<div class="text-xs text-ihh-slate-gray mt-1">
							{{ currentPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
							<span v-if="currentVariant?.optionValues?.length"> / {{ currentVariant.optionValues.map((ov) => ov.label).join(', ') }}</span>
						</div>
						<div class="flex items-center text-ihh-midnight-gray space-x-3 mt-2">
							<button
								@click="decreaseQuantity"
								class="shadow shadow-ihh-midnight-gray/20 flex w-5 h-5 p-1 rounded-full bg-white items-center justify-center cursor-pointer"
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
									<path d="M19 13H5v-2h14v2z" />
								</svg>
							</button>
							<div class="font-semibold text-sm">{{ quantities[currentIndex] }}</div>
							<button
								@click="increaseQuantity"
								class="shadow shadow-ihh-midnight-gray/20 flex w-5 h-5 p-1 rounded-full bg-white items-center justify-center cursor-pointer"
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
									<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
								</svg>
							</button>
						</div>
					</div>
				</div>

				<!-- Product options -->
				<div class="space-y-3">
					<div v-for="po in currentProduct.product.productOptions" :key="po.id" class="space-y-2">
						<div class="text-ihh-slate-gray font-semibold text-xs">
							<span class="text-ihh-midnight-gray mr-2 opacity-80">{{ po.displayName }}:</span>
						</div>
						<div class="flex flex-wrap gap-2">
							<ProductOptionButtons
								v-for="o in po.optionValues"
								:key="o.id"
								:option="o"
								:variants="currentProduct.product.variants"
								:selectedOptions="selectedOptions[currentIndex]"
								:small="true"
								:inventoryTracking="currentProduct.product.inventoryTracking === 'variant'"
								@click="selectOption(o)"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Validation message -->
			<div v-if="showValidation" class="text-ihh-coral text-xs font-semibold mt-2 text-center">Please select all required options before continuing.</div>

			<!-- Navigation buttons -->
			<div class="flex w-full justify-between mt-6 gap-3">
				<button
					v-if="currentIndex > 0"
					@click="goBack"
					class="px-6 py-2.5 rounded-lg border-2 border-ihh-dark-salmon text-ihh-midnight-gray text-sm font-semibold hover:bg-ihh-dark-salmon/10 transition-colors duration-200"
				>
					Back
				</button>
				<button
					v-else
					@click="$emit('hide')"
					class="px-6 py-2.5 rounded-lg border-2 border-ihh-gray/50 text-ihh-slate-gray text-sm font-semibold hover:bg-ihh-gray/10 transition-colors duration-200"
				>
					Cancel
				</button>

				<button
					@click="goNext"
					class="px-6 py-2.5 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon text-ihh-midnight-gray text-sm font-semibold transition-colors duration-200"
				>
					{{ isLastProduct ? 'Continue' : 'Next' }}
				</button>
			</div>
		</div>
	</Modal>
</template>

<script>
import Modal from '../layout/modal.vue'
import ProductOptionButtons from './product-option-buttons.vue'

export default {
	components: { Modal, ProductOptionButtons },
	props: {
		products: { type: Array, required: true },
	},
	data() {
		return {
			currentIndex: 0,
			selectedOptions: [],
			quantities: [],
			showValidation: false,
		}
	},
	created() {
		// Initialize quantities for each product
		this.quantities = this.products.map((p) => p.quantity)

		// Initialize selectedOptions for each product
		this.selectedOptions = this.products.map((p) => {
			let options = []
			for (let po of p.product.productOptions || []) {
				for (let o of po.optionValues || []) {
					o.optionId = po.id
				}
				// Pre-select from existing variant if available
				if (p.variant?.optionValues?.length) {
					let selectedFromVariant = po.optionValues.filter((o) => p.variant.optionValues.find((vo) => vo.id === o.id))
					options = [...options, ...selectedFromVariant]
				} else {
					// Select default option if available
					let defaultOption = po.optionValues?.find((o) => o.isDefault)
					if (defaultOption) options.push(defaultOption)
				}
			}
			return options
		})
	},
	computed: {
		currentProduct() {
			return this.products[this.currentIndex]
		},
		isLastProduct() {
			return this.currentIndex === this.products.length - 1
		},
		currentVariant() {
			let product = this.currentProduct.product
			let options = this.selectedOptions[this.currentIndex]
			return product.variants?.find((v) => v.optionValues?.every((o) => options?.find((so) => so.id === o.id)))
		},
		currentPrice() {
			return this.currentVariant?.currentPrice || this.currentProduct.product?.currentPrice
		},
		currentThumbnail() {
			let product = this.currentProduct.product
			return product?.images?.find((i) => i?.isThumbnail)?.url || product?.images?.[0]?.url || '/images/obDefaultProductImage-min.jpg'
		},
		hasAllOptionsSelected() {
			let product = this.currentProduct.product
			let options = this.selectedOptions[this.currentIndex] || []
			return product.productOptions?.every((po) => options.find((o) => o.optionId === po.id))
		},
	},
	methods: {
		selectOption(option) {
			if (!option) return
			let options = (this.selectedOptions[this.currentIndex] || []).filter((o) => o.optionId !== option.optionId)
			this.selectedOptions.splice(this.currentIndex, 1, [...options, option])
			this.showValidation = false
		},
		goBack() {
			this.showValidation = false
			this.currentIndex--
		},
		goNext() {
			if (!this.hasAllOptionsSelected) {
				this.showValidation = true
				return
			}
			this.showValidation = false

			if (this.isLastProduct) {
				// Resolve variants for all products and emit
				let resolvedProducts = this.products.map((p, i) => {
					let options = this.selectedOptions[i]
					let variant = p.product.variants?.find((v) => v.optionValues?.every((o) => options?.find((so) => so.id === o.id)))
					return { ...p, variant, quantity: this.quantities[i] }
				})
				this.$emit('confirm', resolvedProducts)
			} else {
				this.currentIndex++
			}
		},
		decreaseQuantity() {
			if (this.quantities[this.currentIndex] > 0) {
				this.quantities.splice(this.currentIndex, 1, this.quantities[this.currentIndex] - 1)
			}
		},
		increaseQuantity() {
			this.quantities.splice(this.currentIndex, 1, this.quantities[this.currentIndex] + 1)
		},
		onImgError(e) {
			e.target.src = '/images/obDefaultProductImage-min.jpg'
		},
	},
}
</script>
